import { Capacitor } from '@capacitor/core'
import { CapacitorCalendar, CalendarPermissionScope, type CalendarEvent } from '@ebarooni/capacitor-calendar'

/**
 * iOS Calendar Integration
 * Handles permissions, event creation, and calendar synchronization
 */

export interface IosCalendarEvent {
  title: string
  startDate: number // timestamp in milliseconds
  endDate: number // timestamp in milliseconds
  location?: string
  description?: string
  calendarId?: string
}

export interface IosCalendarState {
  isConnected: boolean
  hasPermission: boolean
  calendarId?: string
  lastSynced?: Date
}

/**
 * Check if running on native iOS platform
 */
export const iosIsNative = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
}

/**
 * Check if running on iOS (web or native)
 */
export const iosIsPlatform = (): boolean => {
  return iosIsNative() || /iPad|iPhone|iPod/i.test(navigator.userAgent)
}

/**
 * Request calendar permissions
 */
export const iosRequestCalendarPermission = async (): Promise<boolean> => {
  try {
    if (!iosIsNative()) {
      console.log('[IosCalendar] Not running on native iOS')
      return false
    }

    const result = await CapacitorCalendar.requestPermission({
      scope: CalendarPermissionScope.WRITE_CALENDAR
    })

    return result.result === 'granted'
  } catch (error) {
    console.error('[IosCalendar] Error requesting permissions:', error)
    return false
  }
}

/**
 * Check current calendar permissions
 */
export const iosCheckCalendarPermission = async (): Promise<'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'> => {
  try {
    if (!iosIsNative()) {
      return 'denied'
    }

    const result = await CapacitorCalendar.checkPermission({
      scope: CalendarPermissionScope.WRITE_CALENDAR
    })

    return result.result
  } catch (error) {
    console.error('[IosCalendar] Error checking permissions:', error)
    return 'denied'
  }
}

/**
 * Get the default calendar for the device
 */
export const iosGetDefaultCalendar = async (): Promise<string | undefined> => {
  try {
    if (!iosIsNative()) {
      return undefined
    }

    const result = await CapacitorCalendar.getDefaultCalendar()
    return result.result?.id
  } catch (error) {
    console.error('[IosCalendar] Error getting default calendar:', error)
    return undefined
  }
}

/**
 * Create an event in the iOS calendar
 */
export const iosCreateCalendarEvent = async (event: IosCalendarEvent): Promise<string | null> => {
  try {
    if (!iosIsNative()) {
      console.log('[IosCalendar] Not running on native iOS')
      return null
    }

    // Check permissions first
    const permissionState = await iosCheckCalendarPermission()
    if (permissionState !== 'granted') {
      const granted = await iosRequestCalendarPermission()
      if (!granted) {
        throw new Error('Calendar permission denied')
      }
    }

    // Get default calendar if not specified
    let calendarId = event.calendarId
    if (!calendarId) {
      calendarId = await iosGetDefaultCalendar()
    }

    if (!calendarId) {
      throw new Error('No calendar available')
    }

    const result = await CapacitorCalendar.createEvent({
      title: event.title,
      calendarId,
      startDate: event.startDate,
      endDate: event.endDate,
      location: event.location,
      description: event.description,
      isAllDay: false
    })

    console.log('[IosCalendar] Event created:', result.id)
    return result.id
  } catch (error) {
    console.error('[IosCalendar] Error creating event:', error)
    throw error
  }
}

/**
 * Delete an event from the iOS calendar
 */
export const iosDeleteCalendarEvent = async (eventId: string): Promise<boolean> => {
  try {
    if (!iosIsNative()) {
      return false
    }

    await CapacitorCalendar.deleteEvent({
      id: eventId
    })

    console.log('[IosCalendar] Event deleted:', eventId)
    return true
  } catch (error) {
    console.error('[IosCalendar] Error deleting event:', error)
    return false
  }
}

/**
 * List events in a date range
 */
export const iosListCalendarEvents = async (from: Date, to: Date): Promise<CalendarEvent[]> => {
  try {
    if (!iosIsNative()) {
      return []
    }

    const result = await CapacitorCalendar.listEventsInRange({
      from: from.getTime(),
      to: to.getTime()
    })

    return result.result || []
  } catch (error) {
    console.error('[IosCalendar] Error listing events:', error)
    return []
  }
}

/**
 * Open the iOS calendar app
 */
export const iosOpenCalendarApp = async (date?: Date): Promise<void> => {
  try {
    if (!iosIsNative()) {
      console.log('[IosCalendar] Not running on native iOS')
      return
    }

    await CapacitorCalendar.openCalendar({
      date: date?.getTime() || Date.now()
    })
  } catch (error) {
    console.error('[IosCalendar] Error opening calendar:', error)
    throw error
  }
}

